import { useEffect, useRef, useState } from "react";

/**
 * SmartImage — list-friendly <img> wrapper that beats the default
 * "lazy loads only when on-screen" pop-in.
 *
 * Strategy:
 *   - First N cards (`priority=true`): loaded eagerly with
 *     `fetchpriority="high"` so the LCP photo doesn't wait on JS.
 *   - Everything else: an IntersectionObserver with a fat `rootMargin`
 *     (default 600px) starts the fetch *before* the card scrolls into
 *     view, so by the time the user gets there, the photo is already in
 *     the browser cache. Smooth, no pop-in.
 *   - Fades in over 200ms on load to mask any decode delay.
 *
 * Falls back gracefully when IntersectionObserver isn't available (just
 * loads the image normally).
 */
export default function SmartImage({
  src,
  alt,
  className = "",
  priority = false,
  rootMargin = "600px",
  ...rest
}) {
  const ref = useRef(null);
  const [loaded, setLoaded] = useState(false);
  // When priority, set src immediately. Otherwise wait until intersection.
  const [actualSrc, setActualSrc] = useState(priority ? src : null);

  useEffect(() => {
    if (priority) {
      setActualSrc(src);
      return undefined;
    }
    if (typeof IntersectionObserver === "undefined" || !ref.current) {
      // No IO support — just load it. (Older browsers / SSR.)
      setActualSrc(src);
      return undefined;
    }
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setActualSrc(src);
          io.disconnect();
        }
      },
      { rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [src, priority, rootMargin]);

  return (
    <img
      ref={ref}
      src={actualSrc || undefined}
      alt={alt}
      onLoad={() => setLoaded(true)}
      // Async decode keeps scrolling fluid while the browser turns the
      // bytes into pixels off the main thread.
      decoding="async"
      // First card on screen wants the fastest possible fetch slot. The
      // browser ignores fetchpriority on lazy images, so it only matters
      // for the priority ones.
      fetchPriority={priority ? "high" : "auto"}
      loading={priority ? "eager" : "lazy"}
      className={`${className} transition-opacity duration-300 ${
        loaded ? "opacity-100" : "opacity-0"
      }`}
      {...rest}
    />
  );
}
